/**
 * useArticleMeta
 *
 * Dynamically updates <meta> tags, <title>, and the JSON-LD <script> block
 * in document.head as the user scrolls through articles.
 *
 * Strategy:
 * - On mount, we stamp every meta tag we manage with data-article-managed="true"
 *   so we can find and overwrite them without touching unrelated tags.
 * - The JSON-LD <script> gets id="article-jsonld" for easy targeting.
 * - We never remove tags, only mutate their content — this avoids layout flicker
 *   and keeps the initial SSR tags intact until the first scroll update.
 */

import { useCallback } from "react";
import { ArticleData } from "@/types/article.types";
import { ContentBlock } from "@/types/common";
import {
  buildRegionalAlternates,
  getPrimaryHreflangForHost,
} from "@/utils/regionalSeo";

// ── Types ─────────────────────────────────────────────────────────────────────

interface ArticleMetaOptions {
  host: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function upsertMeta(
  selector: string,
  attrToSet: "content" | "href",
  value: string,
) {
  if (typeof document === "undefined") return;
  let el = document.querySelector<HTMLMetaElement | HTMLLinkElement>(selector);
  if (!el) {
    // Create it if SSR didn't emit it
    const tag = selector.startsWith("link")
      ? document.createElement("link")
      : document.createElement("meta");
    document.head.appendChild(tag);
    el = tag as HTMLMetaElement;
  }
  (el as any)[attrToSet] = value;
}

function setMeta(name: string, content: string) {
  upsertMeta(`meta[name="${name}"]`, "content", content);
}

function setOgMeta(property: string, content: string) {
  upsertMeta(`meta[property="${property}"]`, "content", content);
}

function setTwitterMeta(name: string, content: string) {
  upsertMeta(`meta[name="${name}"]`, "content", content);
}

function setJsonLd(schema: object) {
  if (typeof document === "undefined") return;
  let el = document.getElementById(
    "article-jsonld",
  ) as HTMLScriptElement | null;
  if (!el) {
    el = document.createElement("script");
    el.id = "article-jsonld";
    el.type = "application/ld+json";
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(schema).replace(/</g, "\\u003c");
}

function getArticleBody(data: ContentBlock[]): string {
  if (!Array.isArray(data)) return "";
  return data
    .map((section) => {
      switch (section.type) {
        case "header":
        case "paragraph":
          return section.content?.replace(/<[^>]+>/g, "") || "";
        case "summary":
        case "blurb":
        case "fact":
        case "blockQuote":
          return section.content || "";
        case "question":
          return `Q. ${section.question || ""}`;
        case "answer":
          return section.answer || "";
        case "question-answer":
          return `Q. ${section.question || ""}\nA. ${section.answer || ""}`;
        case "also-read":
          return section.title || "";
        default:
          return "";
      }
    })
    .filter(Boolean)
    .join("\n\n")
    .trim();
}

// Map CMS post types to valid schema.org Article subtypes. The CMS sends
// names that are not always valid schema.org types, so they must be
// normalized: news -> NewsArticle, blog -> BlogPosting, everything else ->
// Article. Keeps the client JSON-LD consistent with the server-rendered one.
function getSchemaType(postType?: string): string {
  switch ((postType || "").toLowerCase()) {
    case "news":
      return "NewsArticle";
    case "blog":
      return "BlogPosting";
    default:
      return "Article";
  }
}

function buildSchema(article: ArticleData, host: string): object {
  const baseUrl = `https://${host}`;
  const articleUrl = `${baseUrl}${article?.fullpath || ""}`;
  const heroImageUrl = article?.hero_image
    ? new URL(article.hero_image, baseUrl).toString()
    : `${baseUrl}/assets/images/og-image.jpg`;
  const primaryHreflang = getPrimaryHreflangForHost(host);

  const authors = article?.authors?.length ? article.authors : [];
  const authorSchema = authors.length
    ? authors.map((author) => ({
        "@type": "Person",
        name:
          author?.name ||
          author?.full_name ||
          [author?.first_name, author?.last_name].filter(Boolean).join(" ") ||
          "People Matters",
        ...(author?.url
          ? { url: `${baseUrl}/author/${author.url}` }
          : {}),
      }))
    : { "@type": "Organization", name: "People Matters" };

  const hasVideos =
    article?.meta_data?.seo?.content_url ||
    article?.meta_data?.seo?.embed_url ||
    article?.post_type?.name === "VIDEO" ||
    article?.video_url;

  const videoThumbnails =
    article?.meta_data?.seo?.video_thumbnails?.split(",").filter(Boolean) || [];
  const videoDurations =
    article?.meta_data?.seo?.video_durations?.split(",").filter(Boolean) || [];
  const contentUrls =
    article?.meta_data?.seo?.content_url?.split(",").filter(Boolean) || [];
  const embedUrls =
    article?.meta_data?.seo?.embed_url?.split(",").filter(Boolean) || [];

  return {
    "@context": "https://schema.org",
    "@type": getSchemaType(article?.post_type?.name),
    headline: article?.title,
    description: article?.description || undefined,
    articleBody: getArticleBody(article?.json_content || []),
    datePublished: article?.meta_data?.published_time,
    dateModified:
      article?.meta_data?.modified_time || article?.meta_data?.published_time,
    image: [heroImageUrl],
    author: authorSchema,
    publisher: {
      "@type": "Organization",
      name: "People Matters",
      logo: {
        "@type": "ImageObject",
        url: `${baseUrl}/assets/images/logo.png`,
      },
    },
    ...(primaryHreflang ? { inLanguage: primaryHreflang } : {}),
    url: articleUrl,
    mainEntityOfPage: { "@type": "WebPage", "@id": articleUrl },
    ...(hasVideos && {
      video: {
        "@type": "VideoObject",
        name: article?.title,
        description: article?.description || article?.title,
        uploadDate: article?.meta_data?.published_time || undefined,
        thumbnailUrl: videoThumbnails.length > 0 ? videoThumbnails : [heroImageUrl],
        duration: videoDurations.length > 0 ? videoDurations : undefined,
        contentUrl: contentUrls.length > 0 ? contentUrls : undefined,
        embedUrl: embedUrls.length > 0 ? embedUrls : undefined,
        publisher: {
          "@type": "Organization",
          name: "People Matters",
          logo: {
            "@type": "ImageObject",
            url: `${baseUrl}/assets/images/logo.png`,
          },
        },
      },
    }),
  };
}

// ── Hook ──────────────────────────────────────────────────────────────────────

export function useArticleMeta({ host }: ArticleMetaOptions) {
  /**
   * Call this whenever the active article changes.
   * Safe to call on every scroll tick — all operations are O(1) DOM mutations.
   */
  const updateMeta = useCallback(
    (article: ArticleData) => {
      if (typeof document === "undefined" || !article) return;

      const title =
        article.title || "People Matters - Latest HR Trends, News & Articles";
      const metaDescription =
        article?.description ||
        "Discover expert insights and innovative solutions for the daily challenges in Human Resources. Stay updated with the latest HR trends, news, and articles.";
      const ogDescription =
        article?.meta_data?.seo?.og_description ||
        article?.description ||
        "Discover expert insights and innovative solutions for the daily challenges in Human Resources.";
      const imageUrl = article?.hero_image
        ? new URL(article.hero_image, `https://${host}`).toString()
        : `https://${host}/assets/images/og-image.jpg`;
      const twitterImageUrl = article?.meta_data?.seo?.twitter_image
        ? new URL(
            article?.meta_data?.seo?.twitter_image,
            `https://${host}`,
          ).toString()
        : imageUrl;
      // Self-referencing canonical + hreflang ring for the current host,
      // matching the server-rendered metadata. seo.canonical_url is passed as
      // originUrl (used only to choose x-default), not as the canonical.
      const regional = buildRegionalAlternates({
        currentHost: host,
        path: article?.fullpath || "",
        originUrl: article?.meta_data?.seo?.canonical_url,
      });
      const canonicalUrl = regional.canonical;

      // ── <title> ──────────────────────────────────────────────────────────
      document.title = title;

      // ── Standard meta ────────────────────────────────────────────────────
      setMeta("description", metaDescription);
      setMeta(
        "keywords",
        article?.meta_data?.seo?.og_keyworks ||
          "Human Resource Management,HR Best Practices,HR Tools,HR Analytics,HRMS,HRIS,Leadership and Management,HR Magazine India,change management,Hr journal,hr events in India,hr training India,hr workshops India,performance management,talent management,Big Data,B School,Organisation Culture,Staffing,Gen Y,executive coaching,HR law,Labour Law,People Matters Global",
      );
      setMeta("amphtml", `https://${host}/amp${article?.fullpath || ""}`);

      // ── Open Graph ───────────────────────────────────────────────────────
      setOgMeta("og:title", article?.meta_data?.seo?.og_title || title);
      setOgMeta("og:description", ogDescription);
      setOgMeta("og:url", canonicalUrl);
      setOgMeta("og:image", imageUrl);
      setOgMeta("og:image:width", "1200");
      setOgMeta("og:image:height", "630");
      setOgMeta("og:image:alt", title);
      setOgMeta("og:type", "article");
      setOgMeta("og:site_name", "People Matters");
      if (article?.meta_data?.published_time) {
        setOgMeta("article:published_time", article.meta_data.published_time);
      }
      if (article?.meta_data?.modified_time) {
        setOgMeta("article:modified_time", article.meta_data.modified_time);
      }

      // ── Twitter ──────────────────────────────────────────────────────────
      setTwitterMeta(
        "twitter:title",
        article?.meta_data?.seo?.twitter_title || title,
      );
      setTwitterMeta(
        "twitter:description",
        article?.meta_data?.seo?.twitter_description ||
          article?.description ||
          "Discover expert insights and innovative solutions for the daily challenges in Human Resources.",
      );
      setTwitterMeta("twitter:image", twitterImageUrl);
      setTwitterMeta("twitter:image:width", "1200");
      setTwitterMeta("twitter:image:height", "630");
      setTwitterMeta("twitter:image:alt", title);
      setTwitterMeta("twitter:card", "summary_large_image");

      // ── Canonical ────────────────────────────────────────────────────────
      let canonical = document.querySelector<HTMLLinkElement>(
        'link[rel="canonical"]',
      );
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.rel = "canonical";
        document.head.appendChild(canonical);
      }
      canonical.href = canonicalUrl;

      // ── Hreflang ─────────────────────────────────────────────────────────
      // Keep the hreflang ring in sync with the active article's canonical.
      // Tags we manage are stamped data-article-managed so we can rewrite the
      // full set on each update without touching SSR tags from other sources.
      document
        .querySelectorAll('link[rel="alternate"][hreflang][data-article-managed="true"]')
        .forEach((el) => el.parentElement?.removeChild(el));
      if (regional.languages) {
        for (const [hreflang, href] of Object.entries(regional.languages)) {
          const link = document.createElement("link");
          link.rel = "alternate";
          link.hreflang = hreflang;
          link.href = href;
          link.setAttribute("data-article-managed", "true");
          document.head.appendChild(link);
        }
      }

      // ── AMP ──────────────────────────────────────────────────────────────
      let ampLink = document.querySelector<HTMLLinkElement>(
        'link[rel="amphtml"]',
      );
      if (!ampLink) {
        ampLink = document.createElement("link");
        ampLink.rel = "amphtml";
        document.head.appendChild(ampLink);
      }
      ampLink.href = `https://${host}/amp${article?.fullpath || ""}`;

      // ── JSON-LD schema ───────────────────────────────────────────────────
      setJsonLd(buildSchema(article, host));
    },
    [host],
  );

  return { updateMeta };
}
